import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const [polls, setPolls] = useState(0);

  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setStatus("error"); return; }
    if (attempt >= MAX_POLLS) { setStatus("timeout"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setStatus("paid");
        await refreshMe();
        return;
      }
      if (data.status === "expired" || data.payment_status === "failed") { setStatus("error"); return; }
      setTimeout(() => { setPolls(attempt + 1); poll(attempt + 1); }, 2000);
    } catch {
      setStatus("error");
    }
  }, [sessionId, refreshMe]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background noise-bg p-4" data-testid="payment-success-page">
      <div className="max-w-md w-full border border-border bg-card p-8 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin" />
            <h1 className="font-display text-2xl font-black mt-4">Confirming payment…</h1>
            <p className="text-sm text-muted-foreground mt-2">Please wait, verifying with Stripe (attempt {polls + 1}/{MAX_POLLS}).</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-success" />
            <h1 className="font-display text-2xl font-black mt-4">Payment successful</h1>
            <p className="text-sm text-muted-foreground mt-2">Your subscription is active. Thank you!</p>
            <Button className="rounded-none mt-6 w-full" onClick={() => navigate("/")} data-testid="success-dashboard-btn">Go to Dashboard</Button>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-amber-500" />
            <h1 className="font-display text-2xl font-black mt-4">Still processing</h1>
            <p className="text-sm text-muted-foreground mt-2">Your payment is taking longer than usual. It will reflect shortly — check Settings.</p>
            <Button className="rounded-none mt-6 w-full" onClick={() => navigate("/settings")} data-testid="timeout-settings-btn">Back to Settings</Button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-destructive" />
            <h1 className="font-display text-2xl font-black mt-4">Could not confirm payment</h1>
            <p className="text-sm text-muted-foreground mt-2">Something went wrong. If you were charged, contact support.</p>
            <Link to="/settings"><Button variant="outline" className="rounded-none mt-6 w-full">Back to Settings</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
